import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * ActivityAddModal - Modal for adding a custom activity to a phase
 *
 * Features:
 * - Phase-specific default durations
 * - Activity type selection
 * - Optional neuro-technique field
 * - Form validation
 */
const ActivityAddModal = ({ phase, onAdd, onClose }) => {
  const [activity, setActivity] = useState({
    title: '',
    description: '',
    duration_minutes: phase === 'practice' ? 15 : 10,
    neuro_technique: '',
    rationale: '',
    activity_type: ''
  });
  const [errors, setErrors] = useState({});
  const titleRef = useRef(null);

  // Focus title input on mount
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Phase labels
  const phaseLabels = {
    play_1: 'Play 1 - Opening',
    practice: 'Practice - Core Learning',
    play_2: 'Play 2 - Closing'
  };

  // Activity types per phase
  const activityTypes = {
    play_1: [
      { value: 'hook', label: 'Hook / Attention Grabber' },
      { value: 'warm_up', label: 'Warm-up' },
      { value: 'review', label: 'Prior Knowledge Review' },
      { value: 'goal_setting', label: 'Goal Setting' }
    ],
    practice: [
      { value: 'introduction', label: 'Concept Introduction' },
      { value: 'guided_practice', label: 'Guided Practice' },
      { value: 'independent_practice', label: 'Independent Practice' },
      { value: 'skill_building', label: 'Skill Building' }
    ],
    play_2: [
      { value: 'game', label: 'Game / Fun Activity' },
      { value: 'application', label: 'Real-world Application' },
      { value: 'reflection', label: 'Reflection' },
      { value: 'summary', label: 'Summary / Wrap-up' },
      { value: 'preview', label: 'Next Session Preview' }
    ]
  };

  // Common neuro techniques
  const neuroTechniques = [
    'Alerting Cue',
    'Retrieval Practice',
    'Chunking',
    'Scaffolding',
    'Interleaving',
    'Elaboration',
    'Dual Coding',
    'Metacognition',
    'Spaced Practice',
    'Application'
  ];

  const handleChange = (field, value) => {
    setActivity(prev => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!activity.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!activity.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (activity.duration_minutes < 1 || activity.duration_minutes > 60) {
      newErrors.duration_minutes = 'Duration must be between 1-60 minutes';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onAdd(phase, {
      ...activity,
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      order: 999 // Will be normalized by parent
    });
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="sp-modal-overlay" onClick={handleOverlayClick}>
      <div className="sp-modal">
        {/* Header */}
        <div className="sp-modal__header">
          <h3 className="sp-modal__title">Add Activity</h3>
          <span className="sp-modal__phase-badge">{phaseLabels[phase]}</span>
          <button className="sp-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="sp-modal__body">
          {/* Title */}
          <div className="sp-form-group">
            <label className="sp-label">Activity Title *</label>
            <input
              ref={titleRef}
              type="text"
              className={`sp-input ${errors.title ? 'sp-input--error' : ''}`}
              value={activity.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Quick Math Challenge"
            />
            {errors.title && <span className="sp-form-error">{errors.title}</span>}
          </div>

          {/* Description */}
          <div className="sp-form-group">
            <label className="sp-label">Description *</label>
            <textarea
              className={`sp-textarea ${errors.description ? 'sp-textarea--error' : ''}`}
              value={activity.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="What will happen during this activity?"
              rows={3}
            />
            {errors.description && <span className="sp-form-error">{errors.description}</span>}
          </div>

          {/* Duration and Activity Type Row */}
          <div className="sp-form-row">
            <div className="sp-form-group sp-form-group--half">
              <label className="sp-label">Duration (minutes) *</label>
              <input
                type="number"
                className={`sp-input ${errors.duration_minutes ? 'sp-input--error' : ''}`}
                value={activity.duration_minutes}
                onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value) || 5)}
                min="1"
                max="60"
              />
              {errors.duration_minutes && <span className="sp-form-error">{errors.duration_minutes}</span>}
            </div>

            <div className="sp-form-group sp-form-group--half">
              <label className="sp-label">Activity Type</label>
              <select
                className="sp-select"
                value={activity.activity_type}
                onChange={(e) => handleChange('activity_type', e.target.value)}
              >
                <option value="">Select type...</option>
                {activityTypes[phase]?.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Neuro Technique */}
          <div className="sp-form-group">
            <label className="sp-label">
              Neuro Technique
              <span className="sp-label-hint">(optional)</span>
            </label>
            <select
              className="sp-select"
              value={activity.neuro_technique}
              onChange={(e) => handleChange('neuro_technique', e.target.value)}
            >
              <option value="">Select technique...</option>
              {neuroTechniques.map(tech => (
                <option key={tech} value={tech}>{tech}</option>
              ))}
            </select>
          </div>

          {/* Rationale */}
          <div className="sp-form-group">
            <label className="sp-label">
              Rationale
              <span className="sp-label-hint">(optional - why this activity?)</span>
            </label>
            <textarea
              className="sp-textarea"
              value={activity.rationale}
              onChange={(e) => handleChange('rationale', e.target.value)}
              placeholder="Why is this activity effective for this student?"
              rows={2}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="sp-modal__footer">
          <button
            type="button"
            className="sp-btn sp-btn--outline"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sp-btn sp-btn--primary"
            onClick={handleSubmit}
          >
            Add Activity
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityAddModal;
